import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Check, Trash2, X, MessageSquare, Gift, Shield, Sparkles, Calendar, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 30000;

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString("fr-FR");
}

const ICONS = {
  review: MessageSquare,
  benefit: Gift,
  event: Calendar,
  admin: Shield,
  match: Target,
  default: Sparkles,
};

// Subtle ping using Web Audio API — no external asset needed
function playPing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.26);
    setTimeout(() => ctx.close(), 400);
  } catch (e) { /* silent */ }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false);
  const prevUnreadRef = useRef(0);
  const ref = useRef(null);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/notifications/unread-count`, { withCredentials: true });
      setUnread(prev => {
        if (data.count > prevUnreadRef.current && prevUnreadRef.current !== null && !document.hidden) {
          // New notification arrived
          playPing();
          setPulse(true);
          setTimeout(() => setPulse(false), 1400);
        }
        prevUnreadRef.current = data.count;
        return data.count;
      });
    } catch (e) { /* ignore */ }
  }, []);

  const loadNotifs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/notifications`, { withCredentials: true });
      setNotifs(data);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + polling
  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [loadUnread]);

  // Fetch list when opening
  useEffect(() => {
    if (open) loadNotifs();
  }, [open, loadNotifs]);

  // Click outside
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleNotifClick = async (n) => {
    if (!n.is_read) {
      try {
        await axios.patch(`${API}/api/notifications/${n._id}/read`, {}, { withCredentials: true });
        setNotifs(prev => prev.map(x => x._id === n._id ? { ...x, is_read: true } : x));
        setUnread(u => Math.max(0, u - 1));
        prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1);
      } catch (e) { /* ignore */ }
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API}/api/notifications/read-all`, {}, { withCredentials: true });
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
      prevUnreadRef.current = 0;
    } catch (e) { /* ignore */ }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/notifications/${id}`, { withCredentials: true });
      setNotifs(prev => {
        const removed = prev.find(n => n._id === id);
        if (removed && !removed.is_read) {
          setUnread(u => Math.max(0, u - 1));
          prevUnreadRef.current = Math.max(0, prevUnreadRef.current - 1);
        }
        return prev.filter(n => n._id !== id);
      });
    } catch (e) { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell-wrapper">
      <button
        data-testid="notification-bell-button"
        onClick={() => setOpen(o => !o)}
        className={`relative w-10 h-10 flex items-center justify-center rounded-lg border transition-all ${
          open
            ? "border-gold/40 bg-gold/10"
            : "border-white/10 hover:border-gold/30 hover:bg-white/5"
        }`}
        style={{ backgroundColor: open ? undefined : "rgba(11,24,54,0.85)", backdropFilter: "blur(8px)" }}
        aria-label="Notifications"
      >
        <Bell
          size={18}
          className={`text-cbm-secondary ${pulse ? "animate-bounce" : ""}`}
        />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-navy ${pulse ? "animate-ping-once" : ""}`}
            style={{ borderColor: "#071028" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] rounded-xl overflow-hidden z-50 animate-fade-in-up"
          style={{
            background: "#1A2E5C",
            border: "1px solid rgba(184,196,224,0.16)",
            boxShadow: "0 12px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(201,162,39,0.08)",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b cbm-divider">
            <div className="flex items-center gap-2">
              <h3 className="font-outfit font-semibold text-white text-sm">Notifications</h3>
              {unread > 0 && (
                <span className="text-xs text-gold font-medium">· {unread} non lue{unread > 1 ? "s" : ""}</span>
              )}
            </div>
            {unread > 0 && (
              <button
                data-testid="mark-all-read-button"
                onClick={markAllRead}
                className="text-xs text-cbm-muted hover:text-gold transition-colors flex items-center gap-1"
              >
                <Check size={12} /> Tout marquer lu
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center">
                <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : notifs.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={28} className="text-cbm-muted mx-auto mb-2 opacity-60" />
                <p className="text-cbm-secondary text-sm">Aucune notification</p>
                <p className="text-cbm-muted text-xs mt-1">Vous serez alerté ici des nouveautés.</p>
              </div>
            ) : (
              notifs.slice(0, 10).map(n => {
                const Icon = ICONS[n.type] || ICONS.default;
                return (
                  <div
                    key={n._id}
                    data-testid={`notification-item-${n._id}`}
                    onClick={() => handleNotifClick(n)}
                    className={`px-4 py-3 border-b cbm-divider cursor-pointer transition-colors group ${
                      !n.is_read ? "bg-gold/[0.06]" : "hover:bg-white/[0.03]"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        !n.is_read ? "bg-gold/15 border border-gold/30" : "bg-white/5 border border-white/10"
                      }`}>
                        <Icon size={14} className={!n.is_read ? "text-gold" : "text-cbm-muted"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-snug truncate ${!n.is_read ? "text-white font-semibold" : "text-cbm-secondary font-medium"}`}>
                            {n.title}
                          </p>
                          {!n.is_read && (
                            <span className="w-2 h-2 rounded-full bg-gold flex-shrink-0 mt-1.5" title="Non lue" />
                          )}
                        </div>
                        <p className="text-cbm-muted text-xs mt-0.5 line-clamp-2">{n.content}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-cbm-muted text-[11px]">{timeAgo(n.created_at)}</span>
                          <button
                            data-testid={`delete-notification-${n._id}`}
                            onClick={(e) => remove(e, n._id)}
                            className="opacity-0 group-hover:opacity-100 text-cbm-muted hover:text-red-400 transition-all p-1"
                            aria-label="Supprimer"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifs.length > 10 && (
            <div className="px-4 py-2 border-t cbm-divider text-center">
              <span className="text-cbm-muted text-xs">Affichage des 10 plus récentes</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
